import _get from 'lodash/get';
import _set from 'lodash/set';
import _cloneDeep from 'lodash/cloneDeep';
import {
    Communication,
    FreewheelPlugin,
    DfpPlugin,
    OmniturePlugin,
    ChartBeatPlugin,
    ConvivaPlugin
} from 'mlb-player-plugins';
import {
    MediaPlayerEvents
} from 'mlb-media-player';
import pageMeta from 'pageMeta/pageMeta';
import DfpConfigUtil from '../advertising/dfp-config-util';
import defaultWiretapVideoJson from './analytics/omniture/tracking-video.json';
/**
 *
 * @module PluginConfigHelper
 * @desc
 * @class PluginConfigHelper
 *
 */
class PluginConfigHelper {

    static getBandwagonConfig(mediaConfig, playerId) {

        const bandwagonAdapterConfig = _get(mediaConfig, 'videocore.events.bandwagonAdapter');

        if(!bandwagonAdapterConfig || !bandwagonAdapterConfig.enabled) {

            return;

        }

        const bandwagonAdapterPluginConfig = {
            name: 'BandwagonAdapter',
            type: 'COMMUNICATION',
            pluginReference: Communication.BandwagonAdapter,
            bandwagonKey: playerId,
            targetPlayerEvents: _get(bandwagonAdapterConfig, 'targetPlayerEvents', {}),
            targetPluginEvents: _get(bandwagonAdapterConfig, 'targetPluginEvents', {}),
        };

        mediaConfig.pluginConfigs.push(bandwagonAdapterPluginConfig);

    }

    static getEventReplicatorConfig(mediaConfig, playerId) {

        const replicatorConfig = _get(mediaConfig, 'videocore.events.replicator');

        if(!replicatorConfig || !replicatorConfig.enabled) {

            return;

        }

        const eventReplicatorPluginConfig = {
            name: 'EventReplicator',
            type: 'COMMUNICATION',
            pluginReference: Communication.EventReplicator,
            parentWindow: window.parent,
            playerId: playerId,
            bandwagonKey: playerId,
            eventsToReplicate: _get(replicatorConfig, 'eventsToReplicate', []),
        };

        mediaConfig.pluginConfigs.push(eventReplicatorPluginConfig);

    }

    static getAdvertisingPluginConfig(mediaConfig, playerId) {

        const adConfig = _get(mediaConfig, 'videocore.advertising');
        const isFreewheelSite = _get(adConfig, 'freewheel.isFreeWheelSite');
        const isDfpSite = _get(adConfig, 'dfp.isDFPSite');
        const getDfpParams = _get(adConfig, 'dfp.getDfpParams');
        let adPluginConfig;

        if (isDfpSite) {

            const dfp = _get(adConfig, 'dfp');
            const autoPlay = _get(dfp, 'autoPlay', true);
            const muted = _get(dfp, 'muted', false);

            if(!dfp || !dfp.enabled) {

                return;

            }

            adPluginConfig = {
                name: `DfpPlugin-${playerId}`,
                type: 'ADVERTISING',
                pluginReference: DfpPlugin,
                videoAdContainerId: `ad-container-${playerId}`,
                controls: true,
                autoPlay,
                muted,
                playsinline: true,
                customAdRequestBuilder: _get(adConfig, 'customAdRequestBuilder'),
                dfp,
            };

            /**
             * Add the getDfpParams helper that will be called with every ad request.
             * This is where we'll override, update, and reset values
             */
            adPluginConfig.dfp.getDfpParams = getDfpParams || DfpConfigUtil.getDfpParams;

        } else if (isFreewheelSite) {

            const freewheel = _get(adConfig, 'freewheel');

            if(!freewheel || !freewheel.enabled) {

                return;

            }

            adPluginConfig = {
                name: `FreewheelPlugin-${playerId}`,
                type: 'ADVERTISING',
                pluginReference: FreewheelPlugin,
                videoAdContainerId: `ad-container-${playerId}`,
                controls: true,
                autoPlay: true,
                muted: true,
                playsinline: true,
                customAdRequestBuilder: _get(adConfig, 'customAdRequestBuilder'),
                freewheel: _get(mediaConfig, 'videocore.advertising.freewheel'),
            };

        } else {

            console.info('No advertising plugin configuration found.');
            return;

        }

        mediaConfig.pluginConfigs.push(adPluginConfig);

    }

    static getWireTapConfigForPlayer(wireTapConfigJSON, playerId) {

        const containers = {};

        containers[`#${playerId}`] = _get(wireTapConfigJSON, 'containers[\'${playerId}\']');

        _set(wireTapConfigJSON, 'containers', containers);

        return wireTapConfigJSON;

    }

    static getOmniturePluginConfig(mediaConfig, playerId) {

        const omniture = _get(mediaConfig, 'videocore.tracking.omniture');
        const wiretapVideoJson = _get(omniture, 'wiretapVideoJson', defaultWiretapVideoJson);
        const wireTapConfig = PluginConfigHelper.getWireTapConfigForPlayer(_cloneDeep(wiretapVideoJson), playerId);

        if(!omniture || !omniture.enabled) {

            return;

        }

        _set(omniture, 'wiretapVideoJson', wireTapConfig);

        const omnitureConfig = {
            pluginReference: OmniturePlugin,
            name: 'OmniturePlugin',
            type: 'ANALYTICS',
            omniture,
        };

        mediaConfig.pluginConfigs.push(omnitureConfig);

    }

    static getChartBeatPluginConfig(mediaConfig) {

        const chartBeatConfig = _get(mediaConfig, 'videocore.tracking.chartBeat');
        if(!chartBeatConfig || !chartBeatConfig.enabled) {

            return;

        }

        _set(chartBeatConfig, 'pluginReference', ChartBeatPlugin);

        mediaConfig.pluginConfigs.push(chartBeatConfig);

    }

    static generateAssetName(metadata) {

        let assetName = '{date} - {title} | {id}';
        for (let contentMetaItemKey in metadata) {

            let contentMetaItemValue = metadata[contentMetaItemKey];

            if (contentMetaItemKey === 'date') {

                contentMetaItemValue = new Date(contentMetaItemValue);
                var contentMetaItemValueYear = contentMetaItemValue.getUTCFullYear();
                var contentMetaItemValueMonth = (contentMetaItemValue.getUTCMonth() + 1).toLocaleString('en-US', {
                    minimumIntegerDigits: 2,
                    useGrouping: false,
                });
                var contentMetaItemValueDate = contentMetaItemValue.getUTCDate().toLocaleString('en-US', {
                    minimumIntegerDigits: 2,
                    useGrouping: false,
                });
                contentMetaItemValue = (contentMetaItemValueYear + '-' + contentMetaItemValueMonth + '-' + contentMetaItemValueDate);
                assetName = assetName.replace(new RegExp('\{' + contentMetaItemKey + '\}', 'g'), contentMetaItemValue);

            } else {

                assetName = assetName.replace(new RegExp('\{' + contentMetaItemKey + '\}', 'g'), contentMetaItemValue);

            }

        }

        return assetName;

    };

    static getConvivaPluginConfig(mediaConfig) {

        const conviva = _get(mediaConfig, 'videocore.tracking.conviva');
        const generateAssetName = _get(conviva, 'generateAssetName') || PluginConfigHelper.generateAssetName;

        if(!conviva || !conviva.enabled) {

            return;

        }

        const convivaConfig = {
            name: 'ConvivaPlugin',
            type: 'ANALYTICS',
            pluginReference: ConvivaPlugin,
            generateAssetName,
            conviva,
        };

        mediaConfig.pluginConfigs.push(convivaConfig);

    }

}

export default PluginConfigHelper;
